// src/apiClient.js
import axios from 'axios';
import store from './store'; // Import the store to access the JWT token

// Create a configured Axios instance
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'https://isamur.ai/api',
  // No need for withCredentials since we're using JWT tokens
});

// Add a request interceptor to include the JWT token in the Authorization header
apiClient.interceptors.request.use(
  config => {
    const token = store.getters.accessToken;
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Add a response interceptor to handle 401 Unauthorized errors globally
apiClient.interceptors.response.use(
  response => response, // Pass through successful responses
  error => {
    if (error.response && error.response.status === 401) {
      // Only a 401 on a request that actually CARRIED a token means the session
      // is dead. Anonymous calls (e.g. the system-info ribbon firing on the login
      // page before the OAuth token is committed) also 401 — treating those as
      // "session dead" wiped the token users had JUST received from Google and
      // bounced them back to /login in a loop (2026-07-09).
      const sentAuth = error.config && error.config.headers && error.config.headers['Authorization'];
      if (!sentAuth) return Promise.reject(error);
      console.warn('401 Unauthorized - Logging out and redirecting to login');
      
      // Clear auth state
      store.dispatch('logout');
      
      // Redirect to login page (only if not already on login page)
      const base = process.env.BASE_URL || '/cockpit/';
      if (window.location.pathname !== base + 'login') {
        window.location.href = base + 'login';
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Transform a media URL to include JWT token for authenticated access.
 * Handles both /api/media/ URLs (secure) and legacy /media/ URLs (transforms them).
 * 
 * @param {string|null} url - The media URL from API response
 * @returns {string|null} - URL with JWT token appended, or null if no URL
 * 
 * Usage in Vue components:
 *   import { secureMediaUrl } from '@/apiClient';
 *   
 *   // In template: :src="secureMediaUrl(item.image_url)"
 *   // In computed: return secureMediaUrl(this.outputMedia?.url);
 */
export function secureMediaUrl(url) {
  if (!url) return null;

  const token = store.getters.accessToken;
  if (!token) return url;

  // Don't re-append if it's already present
  if (url.includes('token=')) return url;

  // If it's already a /api/media/ URL, just append token
  if (url.startsWith('/api/media/')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${token}`;
  }

  // Handle absolute URLs that point to our media directory
  if (url.includes('/media/')) {
    const parts = url.split('/media/');
    const path = parts[parts.length - 1];
    return `/api/media/${path}?token=${token}`;
  }

  // For absolute URLs or unexpected formats, return as-is
  return url;
}

export default apiClient;
